import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    onSnapshot, 
    doc,
    getDoc,
    orderBy,
    updateDoc
} from 'firebase/firestore';

import { db } from '../../../../../firebase/config';
import { useAuth } from '../../../../hooks/useAuth';

import { useTableState } from '../../hooks/useTableState';
import { useDataHydration } from '../../hooks/useDataHydration';

/**
 * useBloqueosArtTable - Cerebro modular de negocio para el Tablero de Bloqueos/Protección ART.
 * Sincroniza y aplana bloqueos en tiempo real con resolución dinámica de paths y ruteo asimétrico.
 */
export const useBloqueosArtTable = () => {
    const { user, profile } = useAuth();
    const isFullUser = true;

    // --- ESTADOS DE CONTROL DE VISTA ---
    const [view, setView] = useState('list'); 
    const [isGrouped, setIsGrouped] = useState(false); 
    const [searchTerm, setSearchTerm] = useState(''); 

    // --- ESTADOS DE BASE DE DATOS ---
    const [realTimeCotizaciones, setRealTimeCotizaciones] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ENTIDADES SELECCIONADAS ---
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isProspect, setIsProspect] = useState(false);
    const activeItemPathRef = useRef(null);

    // --- LÍMITE DE 6 MESES ATRÁS ---
    const limiteFecha = useMemo(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 6);
        return date.toISOString().split('T')[0];
    }, []);

    // --- 1. HOOK DE HIDRATACIÓN EN BACKGROUND ---
    const { clientsCache } = useDataHydration(realTimeCotizaciones);

    // --- 2. LISTENER EN TIEMPO REAL CON EXTRACCIÓN DINÁMICA ---
    useEffect(() => {
        if (!db) return;
        const q = query(collectionGroup(db, 'cotizaciones_art'), orderBy('fechaCarga', 'desc'));
        
        const unsubscribe = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => {
                const docData = d.data();
                const pathParts = d.ref.path.split('/');
                
                // Parser dinámico inmune a prefijos de sandboxing o barras iniciales
                const clientIndex = pathParts.findIndex(p => p === 'clientes' || p === 'prospectos');
                const cliId = clientIndex !== -1 
                    ? pathParts[clientIndex + 1] 
                    : (docData.clienteId || docData.entidadId);

                let sortDate = null; 
                if (docData.cotizaciones && Array.isArray(docData.cotizaciones)) {
                    const bloqueadas = docData.cotizaciones.filter(o => o.tieneFechaBloqueo || o.fechaBloqueo);
                    if (bloqueadas.length > 0) {
                        bloqueadas.sort((a, b) => new Date(a.fechaBloqueo) - new Date(b.fechaBloqueo));
                        sortDate = bloqueadas[0].fechaBloqueo;
                    }
                }

                return {
                    id: d.id,
                    path: d.ref.path,
                    ...docData,
                    clienteId: cliId,
                    clienteNombre: docData.clienteNombre || docData.entidadNombre || '',
                    clienteCuit: docData.clienteCuit || docData.entidadCuit || '',
                    canal: docData.canal || docData.tipoCanal || docData.canalOrigen || '-',
                    canalNombre: docData.canalNombre || docData.nombreCanal || docData.canalNombreId || '-',
                    subcanalNombre: docData.subcanalNombre || docData.subcanalAsociado || docData.subcanalNombreId || '-',
                    sortDate 
                };
            });

            const bloqueadosOnly = data.filter(doc => 
                doc.cotizaciones?.some(o => o.tieneFechaBloqueo || (o.fechaBloqueo && o.fechaBloqueo !== '') )
            );

            setRealTimeCotizaciones(bloqueadosOnly);
            setLoading(false);
        }, (error) => {
            console.error("Error cargando bloqueos ART:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // --- 3. MAPEO, FILTRADO DE 6 MESES Y DETALLE DEL APLANADOR SÍNCRONO ---
    const itemsTable = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase().trim();
        
        const filteredByTime = realTimeCotizaciones.filter(item => {
            if (lowerSearch.length >= 3) return true;
            return item.fechaCarga >= limiteFecha;
        });

        if (isGrouped) {
            return filteredByTime.map(item => {
                const c = clientsCache[item.clienteId] || {};
                let nombreFinal = `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim();
                if (!nombreFinal) {
                    nombreFinal = c.persona === 'JURIDICA' ? (c.nombre || '') : `${c.nombre || ''} ${c.apellido || ''}`.trim();
                }

                return {
                    ...item,
                    displayNombre: (nombreFinal || 'S/D').toUpperCase(),
                    displayDoc: item.clienteCuit || item.clienteDni || c.cuit || c.dni || '-',
                    sortDate: item.sortDate || item.fechaCarga || '',
                    isGroupedRow: true
                };
            });
        } else {
            const expandedItems = [];
            filteredByTime.forEach(parentDoc => {
                const blockages = parentDoc.cotizaciones?.filter(o => o.tieneFechaBloqueo || o.fechaBloqueo) || [];
                const c = clientsCache[parentDoc.clienteId] || {};
                
                let nombreFinal = `${parentDoc.clienteNombre || ''} ${parentDoc.clienteApellido || ''}`.trim();
                if (!nombreFinal) {
                    nombreFinal = c.persona === 'JURIDICA' ? (c.nombre || '') : `${c.nombre || ''} ${c.apellido || ''}`.trim();
                }

                blockages.forEach((b, idx) => {
                    expandedItems.push({
                        ...parentDoc,
                        id: `${parentDoc.id}_${b.compania}_${b.fechaBloqueo || idx}`, 
                        parentDocId: parentDoc.id, 
                        path: parentDoc.path,
                        displayNombre: (nombreFinal || 'S/D').toUpperCase(),
                        displayDoc: parentDoc.clienteCuit || parentDoc.clienteDni || c.cuit || c.dni || '-',
                        
                        companiaBloqueo: b.compania,
                        fechaBloqueo: b.fechaBloqueo || '',
                        tieneFechaBloqueo: !!b.tieneFechaBloqueo,
                        estadoFila: b.estadoRow || b.estadoCotizacion,
                        sortDate: b.fechaBloqueo || '',
                        isGroupedRow: false
                    });
                });
            });
            return expandedItems;
        }
    }, [realTimeCotizaciones, isGrouped, limiteFecha, clientsCache, searchTerm]);

    // --- 4. EVALUACIÓN SÍNCRONA DE HIDRATACIÓN ---
    const needsHydration = useMemo(() => {
        if (realTimeCotizaciones.length === 0) return false;
        return realTimeCotizaciones.some(p => !clientsCache[p.clienteId]);
    }, [realTimeCotizaciones, clientsCache]);

    // --- 5. HOOK DE CONTROL SÍNCRONO DE TABLAS DE GESTIÓN ---
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'sortDate', direction: 'ascending' },
        initialColumns: {
            fechaCarga: true, displayNombre: true, displayDoc: true, numeroCotizacion: true,
            tipoEntidad: true, tipoGestion: true, proteccionComercial: true, companiaBloqueo: true,
            fechaBloqueo: true, estadoProteccion: true, canal: true, canalNombre: true,
            subcanalNombre: true, estadoCotizacion: true,
        },
        recordsPerPage: 100
    });

    const handleFilterChange = useCallback((vals) => {
        setSearchTerm(vals?.search || '');
        tableState.handleFilterChange(vals);
    }, [tableState]);

    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al desbloquear:", e); }
    }, []);

    // --- 6. INTERCEPTOR DE ACCIONES (RUTEA SI ES AUMENTO / MANTIENE INLINE SI ES VENTA) ---
    const handleAction = useCallback(async (item, type, navigate) => {
        const realItem = {
            ...item,
            id: item.parentDocId || item.id 
        };
        
        if (type === 'VIEW') {
            // Replicamos el comportamiento máster de Cotizaciones: si es aumento, navega
            if (realItem.tipoGestion === 'Aumento de Tarifa') {
                navigate('/DetalleAumentoArt', { 
                    state: { 
                        cotizacionId: realItem.id, 
                        clienteId: realItem.clienteId, 
                        entidadId: realItem.clienteId, 
                        clientId: realItem.clienteId, 
                        nCotizacion: realItem.numeroCotizacion, 
                        cotizacion: realItem,
                        contratoId: realItem.contratoOrigenId || realItem.contratoVinculadoId || realItem.contratoId,
                        endosoId: realItem.endosoVinculadoId || realItem.endosoId,
                        fromTableroAumentos: true 
                    } 
                });
            } else {
                setSelectedItem(realItem);
                setView('detail');
            }
        }
        
        if (type === 'EDIT') {
            setSelectedItem(realItem);
            const targetId = item.clienteId || item.entidadId;
            const colPath = item.tipoEntidad === 'PROSPECTO' ? 'public/data/prospectos' : 'public/data/clientes';
            
            try {
                const cliSnap = await getDoc(doc(db, colPath, targetId));
                if (cliSnap.exists()) {
                    setSelectedClient({ id: cliSnap.id, ...cliSnap.data() });
                } else {
                    setSelectedClient({ 
                        id: targetId, 
                        nombre: item.clienteNombre, 
                        apellido: item.clienteApellido, 
                        cuit: item.clienteCuit || item.clienteDni 
                    });
                }
                setIsProspect(item.tipoEntidad === 'PROSPECTO');
                setView('edit');
            } catch (error) {
                console.error("Error levantando cliente de bloqueo ART:", error);
            }
        }
    }, []);

    const handleSuccess = useCallback(() => {
        setView('list');
        setSelectedItem(null);
    }, []);

    return {
        view,
        setView,
        loading,
        needsHydration,
        clientsCache,
        selectedItem,
        selectedClient,
        isProspect,
        isGrouped,
        setIsGrouped,
        isFullUser,
        tableState: {
            ...tableState,
            handleFilterChange 
        },
        itemsTable,
        handleAction,
        handleSuccess,
        setSelectedItem,
        activeItemPathRef,
        unlockPoliza
    };
};